import { Injectable, Inject, inject } from '@angular/core';
import { User, UserManager } from 'oidc-client';
import * as Oidc from 'oidc-client';
import {from as fromPromise, Observable, from, of, EMPTY} from 'rxjs';
import { OidcConfig } from '../conf/conf';
import { map, switchMap } from 'rxjs/operators';

/** Logger decorator class adding a prefix before messages */
class OidcPrefixedLogger implements Oidc.Logger {
  constructor(private logger: Oidc.Logger, private prefix = '[oidc-client]') {}
  error(message?: any, ...optionalParams: any[]): void {
    this.logger.error(this.prefix, message, ...optionalParams);
  }
  info(message?: any, ...optionalParams: any[]): void {
    this.logger.info(this.prefix, message, ...optionalParams);
  }
  debug(message?: any, ...optionalParams: any[]): void {
    this.logger.debug(this.prefix, message, ...optionalParams);
  }
  warn(message?: any, ...optionalParams: any[]): void {
    this.logger.warn(this.prefix, message, ...optionalParams);
  }
}

/**
 * Service d'authentification qui utilise la librairie oidc-client
 */

@Injectable()
export class AuthService {
  config = inject(OidcConfig);

  private manager: UserManager;
  private user: User;


  constructor() {
    this.manager = new UserManager(this.config.userManagers[this.config.provider]);
    this.user = null;
    Oidc.Log.logger = new OidcPrefixedLogger(console);
    Oidc.Log.level = this.config.log_level ? Oidc.Log[this.config.log_level] : Oidc.Log.NONE;
  }

  // MAYBE NOT USED
  getClaims(): any {
    return this.user.profile;
  }

  // MAYBE NOT USED
  getAuthorizationHeaderValue(): string {
    return `${this.user.token_type} ${this.user.access_token}`;
  }

  getUser(): Observable<User> {
    return fromPromise(this.manager.getUser());
  }

  /**
   * Retourne le token à utiliser pour appeler les services backend.
   * Retourne null si l'utilisateur n'est pas connecté ou si le token
   * est expiré
   */
  getToken(): Observable<string> {
    return fromPromise(this.manager.getUser()).pipe(
      map(user => {
        if (user && !user.expired && user.id_token) {
          return user.id_token;
        } else {
          return null;
        }
      })
    );
  }

  getUserStoreUrl(): Observable<string> {
    return fromPromise(<Promise<any>>this.manager.settings.userStore.get(
`returnUrl:${this.manager.settings.authority}:${this.manager.settings.client_id}`));
  }

  setUserStoreUrl(url: string): void {
    this.manager.settings.userStore.set(
      `returnUrl:${this.manager.settings.authority}:${this.manager.settings.client_id}`, url);
  }

  /**
   * Débute le processus d'authentification.
   * Le processus sera terminé lorsque la méthode
   * completeAuthentication sera appelée.
   *
   * @param returnUrl url vers laquelle rediriger l'utilisateur
   * si l'authentification a été réussie et si
   * elle a été effectuée via une popup (si OidcConfigParameters#redirect
   * est positionnée à false)
   * @return l'url vers lequel rediriger l'utilisateur ou null
   * si pas besoin de redirection
   */
  startAuthentication(returnUrl: string ): Observable<string> {
    this.setUserStoreUrl(returnUrl);
    return this.config.redirect
      ? fromPromise(this.manager.signinRedirect())
        .pipe(
          map(_ => null as string),
        )
      : fromPromise(this.manager.signinPopup()).pipe(
          map(_ => returnUrl)
      );
  }

  signinSilentCallback(): Observable<User | undefined> {
    return from(this.manager.signinSilentCallback());
  }

  completeAuthentication(): Observable<string> {
    const returnUrl = this.getUserStoreUrl();
    return this.config.redirect
      ? fromPromise(this.manager.signinRedirectCallback()).pipe(
        switchMap(user =>
          this.getUserStoreUrl().pipe(
            map(url => {
              // évite que le processus de login redirige vers auth-callback
              // en forçant la redirection vers home
              return url != null && !this.isAuthorizationUrl(url) ? url : this.newGoHome();
            }),
          )
        )
      )
      : fromPromise(this.manager.signinPopupCallback()).pipe(
          map(_ => null as string),
      );
  }

  /**
   * Retourne vrai si l'url est une page utilisé lors du processus de login
   * (auth-callback, logoutSuccess, logout-callback, etc...)
   */
  private isAuthorizationUrl(url: string): boolean {
    // on ignore les options manquante et les pages type index (/)
    const authorizationUris = [
      this.config.logout_success_uri,
      this.config.callback,
      this.config.post_logout_redirect_uri,
    ].filter(e => !!e).filter(e => e !== '/');
    return authorizationUris.some(e => url.endsWith(e));
  }

  public newGoHome(returnUrl?: string): string {
    return returnUrl != null ? `${this.config.home}?${returnUrl}` : this.config.home;
  }

  /**
   * @param local if specified, logouts the user from the application only?
   * Otherwise logouts the user from *both* authorization server and app.
   */
  logout(local?: boolean): Observable<void> {
    if (local) {
      // Déconnexion locale uniquement
      return from(this.manager.removeUser());
    }
    return from(Promise.all( [this.manager.removeUser(),
      this.manager.metadataService
        .getMetadata()
        .then((meta) => location.href = meta.end_session_endpoint)
      ]).then(() => undefined)
    );
  }

}
